import { lazy, Suspense, type ReactNode } from 'react';
import Skeleton from '@mui/material/Skeleton';
import type { PropertyFilterProps } from './PropertyFilter';

/**
 * The property filter (SDD-017 §9) as a shared chunk, fetched on demand.
 *
 * Every list screen of every module renders it, so it belongs to no module's budget: loaded
 * statically, the bundler hoists it into whichever module chunk it picks for the shared workspace
 * code (billing's, since prompt 21) and charges a platform pattern to one team (backbone §3 — a
 * module budget is the INCREMENTAL cost of entering that module). Behind this boundary it is one
 * chunk the first list screen fetches and every other list reuses. Everything it builds on — the
 * Autocomplete, the pickers, the toggle buttons — the shell already loads, so the chunk is the
 * pattern's own code and nothing else.
 *
 * `InfiniteDataGrid` stays static on purpose: behind a lazy boundary the MUI X Data Grid it
 * imports would leave the module chunks for `vendor`, i.e. move INTO the boot download (measured:
 * vendor 251.7 → 370.2 kB gz).
 *
 * The screens import this wrapper from `@eis/patterns`; the stories and tests import the pattern.
 */
const LazyPropertyFilter = lazy(async () => ({
  default: (await import('./PropertyFilter')).PropertyFilter,
}));

export function PropertyFilter(props: PropertyFilterProps): ReactNode {
  return (
    <Suspense fallback={<Skeleton variant="rounded" height={40} />}>
      <LazyPropertyFilter {...props} />
    </Suspense>
  );
}
